import React, { useState } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Grid,
  Paper,
  Badge,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DATE_DISPLAY_FORMAT, displayDate, formatDate, toApiDate } from "../../utils/dateUtils";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Event as EventIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  AccessTime as TimeIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Sample data for leave requests
const sampleLeaveRequests = [
  {
    id: 1,
    studentId: "STU001",
    studentName: "John Smith",
    studentAvatar: "JS",
    rollNo: "2024001",
    class: "CSE-A",
    semester: "3rd",
    leaveType: "Medical",
    fromDate: "2024-01-20",
    toDate: "2024-01-22",
    days: 3,
    reason: "High fever and flu symptoms. Doctor has advised rest for 3 days.",
    attachment: "medical_certificate.pdf",
    appliedOn: "2024-01-19",
    status: "pending",
    parentPhone: "+91 9876543210",
    parentEmail: "parent.smith@email.com",
  },
  {
    id: 2,
    studentId: "STU002",
    studentName: "Emily Johnson",
    studentAvatar: "EJ",
    rollNo: "2024002",
    class: "CSE-A",
    semester: "3rd",
    leaveType: "Personal",
    fromDate: "2024-01-25",
    toDate: "2024-01-25",
    days: 1,
    reason: "Family function - Sister's wedding ceremony.",
    attachment: null,
    appliedOn: "2024-01-18",
    status: "pending",
    parentPhone: "+91 9876543211",
    parentEmail: "parent.johnson@email.com",
  },
  {
    id: 3,
    studentId: "STU003",
    studentName: "Michael Brown",
    studentAvatar: "MB",
    rollNo: "2024003",
    class: "CSE-A",
    semester: "3rd",
    leaveType: "Emergency",
    fromDate: "2024-01-15",
    toDate: "2024-01-17",
    days: 3,
    reason:
      "Grandfather hospitalized in critical condition. Need to travel to hometown.",
    attachment: "travel_ticket.pdf",
    appliedOn: "2024-01-14",
    status: "approved",
    approvedBy: "Dr. Sarah Wilson",
    approvedOn: "2024-01-14",
    remarks: "Approved. Please submit medical documents on return.",
    parentPhone: "+91 9876543212",
    parentEmail: "parent.brown@email.com",
  },
  {
    id: 4,
    studentId: "STU004",
    studentName: "Sarah Davis",
    studentAvatar: "SD",
    rollNo: "2024004",
    class: "CSE-A",
    semester: "3rd",
    leaveType: "Academic",
    fromDate: "2024-01-10",
    toDate: "2024-01-12",
    days: 3,
    reason: "Participating in inter-college coding competition at IIT Delhi.",
    attachment: "event_invitation.pdf",
    appliedOn: "2024-01-05",
    status: "approved",
    approvedBy: "Dr. Sarah Wilson",
    approvedOn: "2024-01-06",
    remarks: "Approved. All the best for the competition!",
    parentPhone: "+91 9876543213",
    parentEmail: "parent.davis@email.com",
  },
  {
    id: 5,
    studentId: "STU005",
    studentName: "James Wilson",
    studentAvatar: "JW",
    rollNo: "2024005",
    class: "CSE-A",
    semester: "3rd",
    leaveType: "Personal",
    fromDate: "2024-01-08",
    toDate: "2024-01-10",
    days: 3,
    reason: "Going for a vacation trip.",
    attachment: null,
    appliedOn: "2024-01-02",
    status: "rejected",
    rejectedBy: "Dr. Sarah Wilson",
    rejectedOn: "2024-01-03",
    remarks: "Rejected. Cannot approve leave during exam preparation period.",
    parentPhone: "+91 9876543214",
    parentEmail: "parent.wilson@email.com",
  },
];

// Class teacher's assigned class info
const classInfo = {
  className: "CSE-A",
  semester: "3rd Semester",
  totalStudents: 60,
  department: "Computer Science Engineering",
};

function LeaveApproval() {
  const [tabValue, setTabValue] = useState(0);
  const [leaveRequests, setLeaveRequests] = useState(sampleLeaveRequests);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState("");
  const [remarks, setRemarks] = useState("");
  const [dateFilter, setDateFilter] = useState(null);
  const [success, setSuccess] = useState("");

  const getFilteredRequests = () => {
    let filtered = leaveRequests;

    // Filter by tab status
    if (tabValue === 0) {
      filtered = filtered.filter((r) => r.status === "pending");
    } else if (tabValue === 1) {
      filtered = filtered.filter((r) => r.status === "approved");
    } else if (tabValue === 2) {
      filtered = filtered.filter((r) => r.status === "rejected");
    }

    // Filter by date
    if (dateFilter) {
      const filterDate = toApiDate(dateFilter);
      if (filterDate) {
        filtered = filtered.filter(
          (r) => r.fromDate <= filterDate && r.toDate >= filterDate
        );
      }
    }

    return filtered;
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setViewDialogOpen(true);
  };

  const handleOpenActionDialog = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setRemarks("");
    setActionDialogOpen(true);
  };

  const handleProcessAction = () => {
    if (!selectedRequest) return;

    const updatedRequests = leaveRequests.map((r) => {
      if (r.id === selectedRequest.id) {
        return {
          ...r,
          status: actionType === "approve" ? "approved" : "rejected",
          [actionType === "approve" ? "approvedBy" : "rejectedBy"]:
            "Current Teacher",
          [actionType === "approve" ? "approvedOn" : "rejectedOn"]: toApiDate(new Date()),
          remarks: remarks,
        };
      }
      return r;
    });

    setLeaveRequests(updatedRequests);
    setActionDialogOpen(false);
    setSuccess(
      `Leave request ${
        actionType === "approve" ? "approved" : "rejected"
      } successfully!`
    );
    setTimeout(() => setSuccess(""), 3000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getLeaveTypeColor = (type) => {
    switch (type) {
      case "Medical":
        return "error";
      case "Personal":
        return "primary";
      case "Emergency":
        return "warning";
      case "Academic":
        return "success";
      default:
        return "default";
    }
  };

  const pendingCount = leaveRequests.filter(
    (r) => r.status === "pending"
  ).length;
  const approvedCount = leaveRequests.filter(
    (r) => r.status === "approved"
  ).length;
  const rejectedCount = leaveRequests.filter(
    (r) => r.status === "rejected"
  ).length;

  const columns = [
    {
      field: "studentInfo",
      headerName: "Student",
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: "primary.main",
              fontSize: "0.875rem",
            }}
          >
            {params.row.studentAvatar}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.studentName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.rollNo}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "leaveType",
      headerName: "Leave Type",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getLeaveTypeColor(params.value)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "duration",
      headerName: "Duration",
      width: 200,
      valueGetter: (params) =>
        `${displayDate(params.row.fromDate)} - ${displayDate(params.row.toDate)}`,
    },
    {
      field: "days",
      headerName: "Days",
      width: 80,
      align: "center",
    },
    {
      field: "appliedOn",
      headerName: "Applied On",
      width: 120,
      valueFormatter: (params) => displayDate(params.value),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewRequest(params.row)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === "pending" && (
            <>
              <Tooltip title="Approve">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleOpenActionDialog(params.row, "approve")}
                >
                  <ApproveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleOpenActionDialog(params.row, "reject")}
                >
                  <RejectIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  return (
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Leave Approval
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage leave requests from students in your class
            </Typography>
          </Box>
          <Card sx={{ px: 3, py: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <SchoolIcon color="primary" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Class Teacher
                </Typography>
                <Typography variant="subtitle1" fontWeight="medium">
                  {classInfo.className} - {classInfo.semester}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: "warning.light", color: "warning.dark" }}>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <Badge badgeContent={pendingCount} color="warning">
                  <TimeIcon fontSize="large" />
                </Badge>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {pendingCount}
                  </Typography>
                  <Typography variant="body2">Pending Requests</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: "success.light", color: "success.dark" }}>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <ApproveIcon fontSize="large" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {approvedCount}
                  </Typography>
                  <Typography variant="body2">Approved This Month</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: "error.light", color: "error.dark" }}>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <RejectIcon fontSize="large" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {rejectedCount}
                  </Typography>
                  <Typography variant="body2">Rejected This Month</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Filters:
              </Typography>
              <DatePicker
                label="Filter by Date"
                value={dateFilter}
                onChange={setDateFilter}
                format={DATE_DISPLAY_FORMAT}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: 200 },
                    inputProps: { inputMode: "numeric", autoComplete: "off" },
                  },
                  field: { clearable: true },
                }}
              />
              <Tabs
                value={tabValue}
                onChange={(e, v) => setTabValue(v)}
                sx={{ ml: "auto" }}
              >
                <Tab
                  label={
                    <Badge
                      badgeContent={pendingCount}
                      color="warning"
                      sx={{ px: 1 }}
                    >
                      Pending
                    </Badge>
                  }
                />
                <Tab label={`Approved (${approvedCount})`} />
                <Tab label={`Rejected (${rejectedCount})`} />
                <Tab label="All" />
              </Tabs>
            </Box>
          </CardContent>
        </Card>

        {/* Leave Requests Table */}
        <Card>
          <CardContent>
            <DataGrid
              rows={getFilteredRequests()}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              autoHeight
              disableSelectionOnClick
              sx={{
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
              }}
            />
          </CardContent>
        </Card>

        {/* View Details Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setViewDialogOpen(false); }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "primary.main" }}>
                {selectedRequest?.studentAvatar}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {selectedRequest?.studentName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedRequest?.rollNo} | {selectedRequest?.class}
                </Typography>
              </Box>
              <Chip
                label={selectedRequest?.status?.toUpperCase()}
                color={getStatusColor(selectedRequest?.status)}
                sx={{ ml: "auto" }}
              />
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedRequest && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Leave Details
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <EventIcon color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Leave Type
                          </Typography>
                          <Typography variant="body2">
                            <Chip
                              label={selectedRequest.leaveType}
                              color={getLeaveTypeColor(
                                selectedRequest.leaveType
                              )}
                              size="small"
                            />
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <TimeIcon color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Duration
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(selectedRequest.fromDate)}{" "}
                            -{" "}
                            {formatDate(selectedRequest.toDate)}{" "}
                            ({selectedRequest.days} days)
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <DescriptionIcon color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Reason
                          </Typography>
                          <Typography variant="body2">
                            {selectedRequest.reason}
                          </Typography>
                        </Box>
                      </Box>
                      {selectedRequest.attachment && (
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          size="small"
                        >
                          Download Attachment: {selectedRequest.attachment}
                        </Button>
                      )}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Parent/Guardian Contact
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Typography variant="body2">
                        📞 {selectedRequest.parentPhone}
                      </Typography>
                      <Typography variant="body2">
                        ✉️ {selectedRequest.parentEmail}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Application Info
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Applied on:{" "}
                      {displayDate(selectedRequest.appliedOn)}
                    </Typography>
                  </Paper>
                </Grid>
                {selectedRequest.status !== "pending" && (
                  <Grid item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor:
                          selectedRequest.status === "approved"
                            ? "success.light"
                            : "error.light",
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        {selectedRequest.status === "approved"
                          ? "Approval Details"
                          : "Rejection Details"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>
                          {selectedRequest.status === "approved"
                            ? "Approved"
                            : "Rejected"}{" "}
                          by:
                        </strong>{" "}
                        {selectedRequest.approvedBy ||
                          selectedRequest.rejectedBy}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Date:</strong>{" "}
                        {formatDate(
                          selectedRequest.approvedOn ||
                            selectedRequest.rejectedOn
                        )}
                      </Typography>
                      {selectedRequest.remarks && (
                        <Typography variant="body2">
                          <strong>Remarks:</strong> {selectedRequest.remarks}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            {selectedRequest?.status === "pending" && (
              <>
                <Button
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleOpenActionDialog(selectedRequest, "reject");
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleOpenActionDialog(selectedRequest, "approve");
                  }}
                >
                  Approve
                </Button>
              </>
            )}
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Approve/Reject Action Dialog */}
        <Dialog
          open={actionDialogOpen}
          onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setActionDialogOpen(false); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {actionType === "approve"
              ? "✅ Approve Leave Request"
              : "❌ Reject Leave Request"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <Alert
                severity={actionType === "approve" ? "success" : "error"}
                sx={{ mb: 2 }}
              >
                You are about to {actionType} the leave request from{" "}
                <strong>{selectedRequest?.studentName}</strong> for{" "}
                <strong>{selectedRequest?.days} days</strong> (
                {selectedRequest?.leaveType} leave).
              </Alert>
              <TextField
                label="Remarks (Optional)"
                multiline
                rows={3}
                fullWidth
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder={
                  actionType === "approve"
                    ? "Add any notes or instructions for the student..."
                    : "Provide reason for rejection..."
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color={actionType === "approve" ? "success" : "error"}
              onClick={handleProcessAction}
            >
              {actionType === "approve" ? "Approve Leave" : "Reject Leave"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
}

export default LeaveApproval;
